import { UniversityRosterRowsQuery, EligibleStudent, UniversityRosterImportWarning } from "./types";
import getAge from "@/lib/ageHelper";
import { buildPATStatus } from "../_utils/patStatus";
import { formatProgramLevel } from "../../../_shared/constants";
import { ColumnDef, Row } from "@tanstack/react-table";
import { buildAvatarDetailsColumn, buildExpandableCountColumn } from "@/components/Table";
import { buildCohortColumn, buildPATStatusColumn, buildActionColumn } from "../../../_shared/_utils";
import type { TableRowMenu } from "@/hooks/useTableRowMenu";
import { getStatusTagColor } from "../../../[universityId]/_utils";
import Icon from "@/components/Icon";
import { copyTextToClipboard } from "@/components/CopyButton";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

export type ImportWarningDetailValue = {
  key: string;
  label: string;
  value: string;
  copyFieldLabel: string;
  copyable: boolean;
  href?: string;
};

const MISSING_PGA_ID = "N/A";
const PGA_ID_WARNING_DETAIL_KEYS = new Set(["pga_id", "existing_pga_id"]);

const KNOWN_WARNING_DETAIL_FIELDS: Record<string, { label: string; copyFieldLabel: string }> = {
  existing_person: { label: "Existing person", copyFieldLabel: "Name" },
  existing_pga_id: { label: "PGA ID", copyFieldLabel: "PGA ID" },
  pga_id: { label: "PGA ID", copyFieldLabel: "PGA ID" },
  phone_number: { label: "Phone number", copyFieldLabel: "Phone" },
  transfer_hours_total: { label: "Transfer hours total", copyFieldLabel: "Transfer hours total" },
  transferHoursTotal: { label: "Transfer hours total", copyFieldLabel: "Transfer hours total" },
  transfer_hours_accepted: { label: "Transfer hours accepted", copyFieldLabel: "Transfer hours accepted" },
  transferHoursAccepted: { label: "Transfer hours accepted", copyFieldLabel: "Transfer hours accepted" },
  hours: { label: "Credit hours", copyFieldLabel: "Credit hours" }
};

const PREFERRED_WARNING_DETAIL_KEYS = [
  "existing_person",
  "existing_pga_id",
  "pga_id",
  "phone_number",
  "transfer_hours_total",
  "transferHoursTotal",
  "transfer_hours_accepted",
  "transferHoursAccepted"
];
const PREFERRED_WARNING_DETAIL_KEY_SET = new Set(PREFERRED_WARNING_DETAIL_KEYS);
const COPYABLE_WARNING_DETAIL_KEYS = new Set([
  "phone_number",
  "existing_pga_id",
  "pga_id",
  "transfer_hours_total",
  "transferHoursTotal",
  "transfer_hours_accepted",
  "transferHoursAccepted",
  "hours"
]);

const SKIPPED_WARNING_DETAIL_KEYS = new Set([
  "existing_person",
  "existing_pga_id",
  "pga_id",
  "transfer_hours_total",
  "transfer_hours_accepted",
  "transferHoursTotal",
  "transferHoursAccepted",
  "hours"
]);

export function isLinkablePgaId(pgaId: unknown): pgaId is string {
  return typeof pgaId === "string" && pgaId.trim() !== "" && pgaId !== MISSING_PGA_ID;
}

export function personOverviewPath(pgaId: string): string {
  return `/people/${pgaId}/person/overview`;
}

export function PersonOverviewLink({ pgaId, className }: { pgaId: string; className?: string }) {
  return (
    <a
      href={personOverviewPath(pgaId)}
      target="_blank"
      rel="noopener noreferrer"
      className={className ?? "text-sys-brand-primary hover:underline"}
      onClick={(event) => event.stopPropagation()}
    >
      {pgaId}
    </a>
  );
}

export function isPersonWillBeUpdatedWarning(warning: UniversityRosterImportWarning): boolean {
  return warning.field === "person" || warning.message === "Person will be updated";
}

export function hasPersonWillBeUpdatedWarning(student: EligibleStudent): boolean {
  return (student.importWarnings ?? []).some(isPersonWillBeUpdatedWarning);
}

function isPresentWarningDetail(value: unknown): boolean {
  return value != null && value !== "";
}

/** Fills in the matched person's PGA ID when a person-update warning omitted it. */
export function resolvedImportWarningDetails(
  warning: UniversityRosterImportWarning,
  student: EligibleStudent
): UniversityRosterImportWarning["details"] {
  const details = { ...(warning.details ?? {}) };
  if (!isPersonWillBeUpdatedWarning(warning)) return details;
  if (!isPresentWarningDetail(details.pga_id) && isLinkablePgaId(student.pgaId)) {
    details.pga_id = student.pgaId;
  }
  return details;
}

function parseOptionalHours(value: unknown): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function personAttribute(personAttributes: unknown, ...keys: string[]): unknown {
  if (!personAttributes || typeof personAttributes !== "object") return undefined;
  const record = personAttributes as Record<string, unknown>;
  for (const key of keys) {
    if (record[key] != null && record[key] !== "") return record[key];
  }
  return undefined;
}

function humanizeWarningDetailKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/** Copyable labeled values for one import warning's details blob. */
export function importWarningDetailValues(
  details?: UniversityRosterImportWarning["details"]
): ImportWarningDetailValue[] {
  if (!details) return [];

  const items: ImportWarningDetailValue[] = [];
  const seenLabels = new Set<string>();

  const push = (key: string, raw: unknown) => {
    if (!isPresentWarningDetail(raw)) return;
    const known = KNOWN_WARNING_DETAIL_FIELDS[key];
    const label = known?.label ?? humanizeWarningDetailKey(key);
    if (seenLabels.has(label)) return;
    seenLabels.add(label);
    const value = String(raw);
    items.push({
      key,
      label,
      value,
      copyFieldLabel: known?.copyFieldLabel ?? label,
      copyable: COPYABLE_WARNING_DETAIL_KEYS.has(key),
      ...(PGA_ID_WARNING_DETAIL_KEYS.has(key) && isLinkablePgaId(value) ? { href: personOverviewPath(value) } : {})
    });
  };

  for (const key of PREFERRED_WARNING_DETAIL_KEYS) {
    if (key in details) push(key, details[key]);
  }

  const hasNamedHours = items.some((item) => item.label.startsWith("Transfer hours"));
  if (!hasNamedHours) push("hours", details.hours);

  for (const [key, value] of Object.entries(details)) {
    if (PREFERRED_WARNING_DETAIL_KEY_SET.has(key)) continue;
    if (key === "hours") continue;
    push(key, value);
  }

  return items;
}

/** Formats known import-warning details for on-screen review and copy. */
export function formatImportWarningDetails(details?: UniversityRosterImportWarning["details"]): string[] {
  if (!details) return [];

  const lines: string[] = [];
  const existingPerson = details.existing_person;
  const existingPgaId = details.existing_pga_id;

  if (typeof existingPerson === "string" && existingPerson) {
    if (typeof existingPgaId === "string" && existingPgaId) {
      lines.push(`Existing person: ${existingPerson} (PGA ID: ${existingPgaId})`);
    } else {
      lines.push(`Existing person: ${existingPerson}`);
    }
  } else if (typeof existingPgaId === "string" && existingPgaId) {
    lines.push(`PGA ID: ${existingPgaId}`);
  } else if (typeof details.pga_id === "string" && details.pga_id) {
    lines.push(`PGA ID: ${details.pga_id}`);
  }

  const totalHours = details.transfer_hours_total ?? details.transferHoursTotal;
  const acceptedHours = details.transfer_hours_accepted ?? details.transferHoursAccepted;
  const hasNamedHours = (totalHours != null && totalHours !== "") || (acceptedHours != null && acceptedHours !== "");

  if (totalHours != null && totalHours !== "") {
    lines.push(`Transfer hours total: ${String(totalHours)}`);
  }
  if (acceptedHours != null && acceptedHours !== "") {
    lines.push(`Transfer hours accepted: ${String(acceptedHours)}`);
  }
  if (!hasNamedHours && details.hours != null && details.hours !== "") {
    lines.push(`Credit hours: ${String(details.hours)}`);
  }

  for (const [key, value] of Object.entries(details)) {
    if (SKIPPED_WARNING_DETAIL_KEYS.has(key)) continue;
    if (value == null || value === "") continue;
    lines.push(`${key}: ${String(value)}`);
  }

  return lines;
}

export function formatTransferHoursCopyText(student: EligibleStudent): string {
  const lines: string[] = [];
  if (student.transferHours != null && student.transferHours > 0) {
    lines.push(`Transfer hours total: ${student.transferHours}`);
  }
  if (student.transferHoursAccepted != null) {
    lines.push(`Transfer hours accepted: ${student.transferHoursAccepted}`);
  }
  return lines.join("\n");
}

export function isRosterPagePaginated(pageInfo?: { hasNextPage?: boolean; hasPreviousPage?: boolean } | null): boolean {
  return Boolean(pageInfo?.hasNextPage || pageInfo?.hasPreviousPage);
}

/** Warning messages and details only — used by the Warnings column copy control. */
export function formatRosterWarningsCopyText(student: EligibleStudent): string {
  const warnings = student.importWarnings ?? [];
  if (warnings.length === 0) return "";

  const lines: string[] = [];
  for (const warning of warnings) {
    lines.push(`- ${warning.message}`);
    for (const detail of formatImportWarningDetails(resolvedImportWarningDetails(warning, student))) {
      lines.push(`  ${detail}`);
    }
  }
  return lines.join("\n");
}

export function formatEligibleStudentCopyText(student: EligibleStudent): string {
  const lines = [student.name, `PGA ID: ${student.pgaId}`];

  if (student.email) lines.push(`Email: ${student.email}`);
  if (student.mobilePhone) lines.push(`Phone: ${student.mobilePhone}`);
  if (student.programLevel) lines.push(`Program level: ${formatProgramLevel(student.programLevel)}`);

  const transferHoursCopy = formatTransferHoursCopyText(student);
  if (transferHoursCopy) lines.push(transferHoursCopy);

  const warningsCopy = formatRosterWarningsCopyText(student);
  if (warningsCopy) {
    lines.push("", "Warnings:", warningsCopy);
  }

  return lines.join("\n");
}

export function formatEligibleStudentsCopyText(students: EligibleStudent[]): string {
  return students.map(formatEligibleStudentCopyText).join("\n\n---\n\n");
}

export const mapEligibleStudents = (data: UniversityRosterRowsQuery | undefined): EligibleStudent[] => {
  if (!data?.university?.universityRosterRows?.edges) return [];

  return data.university.universityRosterRows.edges.map((edge) => {
    const row = edge.node;
    const person = row.person;
    const dateOfBirth = person?.dob
      ? `${person.dob.year}-${String(person.dob.month).padStart(2, "0")}-${String(person.dob.day).padStart(2, "0")}`
      : "";

    const age = person?.dob ? getAge(dateOfBirth) || undefined : undefined;
    const patStatus = buildPATStatus(person?.playingAbilityTestStatus).value;

    const attributedPgaId = personAttribute(row.personAttributes, "pga_id", "pgaId");

    return {
      id: row.id,
      name: person ? `${person.firstName} ${person.lastName}` : "Unknown Student",
      pgaId:
        person?.pgaId || (attributedPgaId != null && attributedPgaId !== "" ? String(attributedPgaId) : "") || "N/A",
      profilePhoto: person?.profilePhoto,
      dateOfBirth,
      email: person?.email || "",
      mobilePhone:
        person?.phoneNumbers?.find((phone) => phone.phoneType === "mobile")?.phoneFormatted ||
        person?.phoneNumbers?.find((phone) => phone.phoneType === "mobile")?.phoneNumber ||
        "",
      programLevel: row.educationLevel,
      cohortStatus: "In-Cohort",
      transferHours: parseOptionalHours(row.transferHours),
      transferHoursAccepted:
        parseOptionalHours(row.transferHoursAccepted) ??
        parseOptionalHours(personAttribute(row.personAttributes, "transfer_hours_accepted", "transferHoursAccepted")),
      hasErrors: row.hasErrors,
      importWarnings: row.importWarnings ?? [],
      contactAddress: person?.contactAddress || null,
      age,
      gender: row.personAttributes?.gender,
      patStatus
    };
  });
};

async function copyStudentDetails(student: EligibleStudent) {
  try {
    await copyTextToClipboard(formatEligibleStudentCopyText(student));
    showSnackbar("Copied student details");
  } catch {
    showErrorSnackbar("Failed to copy student details");
  }
}

type BuildColumnsProps = {
  isCompactLayout: boolean;
  handleViewStudent: (student: EligibleStudent) => void;
  handleOpenProgramLevelDialog: (student: EligibleStudent) => void;
  setDialog: (dialog: { type: "remove"; data: EligibleStudent }) => void;
  setMenu: (menu: TableRowMenu) => void;
  actionId: (rowId: string) => string;
  shouldShowPersonProfileActions: boolean;
};

export function buildColumns({
  isCompactLayout,
  handleViewStudent,
  handleOpenProgramLevelDialog,
  setDialog,
  setMenu,
  actionId,
  shouldShowPersonProfileActions
}: BuildColumnsProps): ColumnDef<EligibleStudent, any>[] {
  return [
    ...buildAvatarDetailsColumn<EligibleStudent>({
      isCompactLayout,
      getTitle: (row) => row.name,
      getSubtitle: (row) => {
        if (shouldShowPersonProfileActions && hasPersonWillBeUpdatedWarning(row) && isLinkablePgaId(row.pgaId)) {
          return (
            <>
              PGA ID: <PersonOverviewLink pgaId={row.pgaId} />
            </>
          );
        }
        return `PGA ID: ${row.pgaId}`;
      },
      getPicture: (row) => row.profilePhoto
    }),
    buildExpandableCountColumn<EligibleStudent>({
      accessorKey: "importWarnings",
      header: "Warnings",
      getCount: (row) => row.importWarnings?.length ?? 0,
      size: 110
    }),
    {
      accessorKey: "programLevel",
      header: "Program / Level",
      cell: ({ row }: { row: Row<EligibleStudent> }) => {
        const hasErrors = row.original.hasErrors;
        const formattedLevel = formatProgramLevel(row.original.programLevel);

        return (
          <div className="flex items-center gap-2">
            {hasErrors && <Icon name="error" size={16} className="text-sys-brand-error" />}
            <span>{formattedLevel}</span>
          </div>
        );
      },
      enableSorting: true
    },
    buildCohortColumn<EligibleStudent>({
      getStatusTagColor,
      getCohortValue: (row) => row.cohortStatus,
      displayMode: "text"
    }),
    buildPATStatusColumn<EligibleStudent>({
      getStatusTagColor,
      getPATStatus: (row) => row.patStatus
    }),
    buildActionColumn<EligibleStudent>({
      getActions: (row) => [
        {
          text: "Copy Student Details",
          onClick: () => {
            void copyStudentDetails(row);
          }
        },
        {
          text: "Change Program Level",
          onClick: () => handleOpenProgramLevelDialog(row)
        },
        {
          text: "Remove from Roster",
          destructive: true,
          iconName: "delete",
          onClick: () => setDialog({ type: "remove", data: row })
        }
      ],
      onViewClick: (row, _event) => handleViewStudent(row),
      actionId,
      setMenu,
      viewButtonTitle: "View Student"
    })
  ];
}
